import type {CustomDealProduct} from '../../../types';

import {CloseOutlined, SearchOutlined} from '@ant-design/icons';
import {Checkbox, Input, Loading} from '@ds/components';
import {cn} from '@ds/helpers';
import Empty from 'common/empty/Empty';
import StockBadge from '../../../components/common/StockBadge/StockBadge';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useAppDispatch, useAppSelector} from 'store';
import {getProducts, resetProducts} from 'store/slices/components';

import {getCustomDealVendorId} from '../../../utils';

type ProductSearchOverlayProps = {
  show: boolean;
  close: () => void;
  onProductSelect: (product: CustomDealProduct) => void;
  onProductDeselect?: (product: CustomDealProduct) => void;
  selectedProductIds?: Set<unknown>;
};

const ProductSearchOverlay: React.FC<ProductSearchOverlayProps> = ({
  show,
  close,
  onProductSelect,
  onProductDeselect,
  selectedProductIds = new Set()
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const dispatch = useAppDispatch();
  const {products, loading} = useAppSelector((state) => state.components);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const vendorId = getCustomDealVendorId();

  useEffect(() => {
    if (show) {
      dispatch(resetProducts());
      setSearchValue('');
      setIsSearching(false);
      setContentHeight(0);
    }
  }, [show, dispatch]);

  useEffect(() => {
    document.body.style.overflow = show ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [show]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      dispatch(resetProducts());
    };
  }, [dispatch]);

  const performSearch = useCallback(
    (searchText: string) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      setIsSearching(true);
      debounceTimer.current = setTimeout(() => {
        void dispatch(
          getProducts({
            vendor_id: vendorId,
            search: searchText || null,
            page_size: 20,
            page: 1
          })
        ).finally(() => {
          setIsSearching(false);
        });
      }, 500);
    },
    [dispatch, vendorId]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);

    if (value.trim().length > 0) {
      performSearch(value);
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    dispatch(resetProducts());
    setIsSearching(false);
  };

  const handleProductToggle = (product: CustomDealProduct, event?: React.MouseEvent) => {
    event?.stopPropagation();
    const isSelected = selectedProductIds.has(product.id);
    if (isSelected) {
      onProductDeselect?.(product);
      return;
    }
    onProductSelect(product);
  };

  const hasResults = Boolean(products?.items?.length);
  const showLoading = loading || isSearching;
  const showEmpty = !showLoading && searchValue.trim().length > 0 && !hasResults;
  const hasContent = showLoading || showEmpty || hasResults;

  useEffect(() => {
    const measureHeight = () => {
      setContentHeight(contentRef.current?.scrollHeight ?? 0);
    };

    requestAnimationFrame(measureHeight);
    const timeoutId = setTimeout(measureHeight, 100);
    return () => clearTimeout(timeoutId);
  }, [showLoading, showEmpty, hasResults, products]);

  if (!show) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="جستجوی محصول"
    >
      <div
        className={cn(
          'flex w-full max-w-150 flex-col overflow-hidden rounded-xl bg-white shadow-lg',
          hasContent ? 'max-h-[90vh]' : 'max-h-22'
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className={cn(
            'flex shrink-0 items-center gap-3 p-4',
            hasContent && 'border-b border-gray-22'
          )}
        >
          <Input
            value={searchValue}
            onChange={handleSearchChange}
            placeHolder="جستجوی محصول..."
            prefix={<SearchOutlined aria-hidden />}
            width="100%"
            autoFocus
          />
          <button
            className="flex items-center justify-center p-2 text-body-lg text-gray-06"
            onClick={close}
            aria-label="بستن"
            type="button"
          >
            <CloseOutlined aria-hidden />
          </button>
        </div>
        <div
          className={cn('overflow-hidden', hasContent ? 'opacity-100' : 'opacity-0')}
          style={{height: contentHeight > 0 ? `${contentHeight}px` : '0px'}}
        >
          <div ref={contentRef} className="max-h-[calc(90vh-88px)] w-full overflow-y-auto">
            {showLoading ? (
              <div className="flex min-h-20 items-center justify-center">
                <Loading size="large" />
              </div>
            ) : null}
            {showEmpty ? (
              <div className="flex min-h-50 items-center justify-center px-5 py-10">
                <Empty>
                  <p>محصولی یافت نشد</p>
                </Empty>
              </div>
            ) : null}
            {hasResults && !showLoading ? (
              <div className="py-2" role="list">
                {(products.items as CustomDealProduct[] | undefined)?.slice(0, 10).map((product) => {
                  const isSelected = selectedProductIds.has(product.id);
                  return (
                    <div
                      key={product.id}
                      className="flex cursor-pointer items-center gap-3 border-b border-gray-02 px-4 py-3 last:border-b-0"
                      onClick={() => handleProductToggle(product)}
                      role="listitem"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleProductToggle(product);
                        }
                      }}
                    >
                      <div className="shrink-0" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleProductToggle(product)}
                        />
                      </div>
                      <div className="flex size-15 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-02">
                        <img className="size-full object-cover" src={product.image} alt="" />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <h3 className="line-clamp-2 text-body-sm-emphasis text-neutral-fg">
                          {product.title}
                        </h3>
                        <div className="flex gap-4">
                          {product.price ? (
                            <span className="text-body-sm-emphasis text-neutral-fg">
                              {product.price.toLocaleString()} تومان
                            </span>
                          ) : null}
                          <StockBadge stock={product.stock} status={product.stock_status} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSearchOverlay;
